// Old path — a BODIED delegate. The derivation now lives at
// `packages/agents/src/data/projects/useLinkableContextItems.ts`
// and is published through the `./data` door; what stays here is the supply of
// the three app-resolved inputs, so the zero-argument signature every caller
// already uses is unchanged.
//
// WHY THE THREE COULD NOT COME. `useQuerySessionsV2` and `useQueryPatientProfiles`
// are both REVIEW-CONTEXT branches — which team member's list you are looking at,
// resolved from app atoms and an RBAC check — and each is app-side by a recorded
// decision on its own domain's carve, not by omission. With object refs on, the
// sessions come from the sidebar's own-sessions list instead, which is app-side
// for the same reason and also carries the offline pending-delete filter. `parseTimeStamp` reads the
// signed-in account's timezone, app state by the same kind of decision. What moved
// is everything those three do not decide: the newest-first sort, the profile
// name-matching, the dedupe, the caps, and the artifact each picked row opens.
import { useOfflinePendingServerDeleteIds } from '@/atoms/offline-recording';
import { patientSexAge } from '@/composition/hosts/chat-object-reference-label';
import { useIsChatObjectRefsEnabled } from '@/features/flags';
import {
  flattenSessionListPages,
  useQuerySessionsList,
  type UseQuerySessionsListParams,
} from '@/features/sessions/hooks/queries/useQuerySessionsList';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useQuerySessionsV2 } from '@/hooks/query/useQuerySessionsV2';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import {
  resolvePatientLabel,
  useLinkableContextItems as useLinkableContextItemsCore,
  type LinkableContextItems,
  type LinkablePatientProfile,
  type LinkableSessionRow,
  type UseLinkableContextItemsArgs as UseLinkableContextItemsCoreArgs,
} from '@heidi/agents/data';
import { useIdentityEditorIntl } from '@heidi/patients';
import { SessionScope } from '@heidi/schema/ml-scribe';
import { withoutPendingServerDeletes } from '@heidi/session/contracts';
import { useQueryLinkedSessionsByProfileId } from '@heidi/session/data';
import { useLatestRef } from '@heidi/utils';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

export { artifactForLinkedItem } from '@heidi/agents/data';
export type { LinkableContextItems } from '@heidi/agents/data';

/**
 * The pre-move argument shape, kept under its old name: the three injected inputs
 * are this file's job, so a caller still passes only `query` and `limit`.
 */
export type UseLinkableContextItemsArgs = Omit<
  UseLinkableContextItemsCoreArgs,
  'parseTimestamp' | 'profiles' | 'sessions' | 'patientSexAge'
> & {
  /**
   * In-session composers only offer the host patient's other sessions, and no
   * patients: the chat is already frozen to that patient.
   */
  patientScope?: { patientProfileId: string; excludeSessionId: string } | null;
};

const NO_PATIENT_ITEMS: LinkableContextItems['patientItems'] = [];

// The sidebar's recent-visits list (v2.2): its `session_name` is the session's
// title, where the older `/sessions/` list still carries the patient's name.
const PICKER_SESSIONS_PARAMS: UseQuerySessionsListParams = {
  scope: SessionScope.me,
  selectedStatuses: [],
  selectedDateRange: {},
  selectedPatientProfiles: [],
  selectedClinicianIds: [],
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  searchQuery: '',
  isUrgentOnly: false,
};

export const useLinkableContextItems = ({
  query,
  limit,
  patientScope,
}: UseLinkableContextItemsArgs = {}): LinkableContextItems => {
  const objectRefs = useIsChatObjectRefsEnabled();
  const { data: firstPageData } = useQuerySessionsV2(!objectRefs && !patientScope);
  const { data: listData } = useQuerySessionsList(PICKER_SESSIONS_PARAMS, {
    pollEnabled: false,
    enabled: objectRefs && !patientScope,
  });
  const pendingServerDeleteIds = useOfflinePendingServerDeleteIds();
  const { data: profiles } = useQueryPatientProfiles();
  const { data: patientSessionsData } = useQueryLinkedSessionsByProfileId(
    patientScope?.patientProfileId ?? ''
  );
  const intl = useIntl();
  // Rebuilt every render, but its formatters only vary with `intl`.
  const identityRef = useLatestRef(useIdentityEditorIntl());
  const sexAge = useCallback(
    (profile: LinkablePatientProfile) =>
      patientSexAge(profile, intl, identityRef.current) ?? undefined,
    [intl, identityRef]
  );

  const listSessions = useMemo(
    () =>
      withoutPendingServerDeletes(
        flattenSessionListPages(listData?.pages),
        pendingServerDeleteIds
      ).map(
        (s): LinkableSessionRow => ({
          session_id: s.session_id,
          session_name: s.session_name,
          created_at: s.created_at,
          created_time_local: s.created_time_local,
          ehr_appt_id: s.ehr_appt_id,
          duration: s.duration,
          patient: {
            name:
              resolvePatientLabel(null, s.patient_profile_name || s.patient?.name) ??
              null,
          },
          patient_profile: s.patient_profile_id ? { id: s.patient_profile_id } : null,
        })
      ),
    [listData?.pages, pendingServerDeleteIds]
  );

  const sessions = useMemo(
    () =>
      patientScope
        ? (patientSessionsData?.sessions ?? []).filter(
            (session) => session.session_id !== patientScope.excludeSessionId
          )
        : objectRefs
          ? listSessions
          : // First page is enough for a glance surface; the dedicated sessions
            // page handles browse-everything cases.
            (firstPageData?.pages?.[0]?.sessions ?? []),
    [patientScope, patientSessionsData, objectRefs, listSessions, firstPageData]
  );

  const items = useLinkableContextItemsCore({
    query,
    limit,
    sessions,
    profiles,
    parseTimestamp: parseTimeStamp,
    patientSexAge: sexAge,
  });

  return useMemo(
    () => (patientScope ? { ...items, patientItems: NO_PATIENT_ITEMS } : items),
    [items, patientScope]
  );
};
